import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pill, Plus, Trash, PencilSimple, Check, X, Archive, ArrowCounterClockwise, CalendarPlus, Camera, Spinner, Storefront, Hash, Copy, CheckCircle, VideoCamera, Barcode, ShieldCheck, Sparkle } from "@phosphor-icons/react";
import {
  listPrescriptions, createPrescription, updatePrescription, deletePrescription,
  getPrescriptionIcsUrl, downloadIcs, scanPrescriptionImage,
} from "@/lib/api";
import { decodeBarcodeFromFile, decodeBarcodeDetailedFromFile } from "@/lib/barcodeScanner";
import { downsizeImage } from "@/lib/imageResize";
import { findDrugMatches } from "@/lib/drugCatalog";
import LiveBarcodeScanner from "@/components/LiveBarcodeScanner";
import RefillVerifyModal from "@/components/RefillVerifyModal";
import RxScanReviewModal from "@/components/RxScanReviewModal";
import MedicationScanAssistant from "@/components/MedicationScanAssistant";
import { rxNextRefillDate, rxDaysUntilRefill, rxStatus, formatRelativeDay } from "@/lib/reminderUtils";
import { toLocalDateInputValue } from "@/lib/dateUtils";
import { showToast } from "@/lib/toast";
import { matchPharmacyPattern } from "@/lib/api";

const STATUS_LABEL = {
  overdue: "OVERDUE",
  due_soon: "REFILL SOON",
  ok: "OK",
  archived: "ARCHIVED",
  unknown: "—",
};
const STATUS_CLASS = {
  overdue: "rem-status rem-status--overdue",
  due_soon: "rem-status rem-status--due",
  ok: "rem-status rem-status--ok",
  archived: "rem-status",
  unknown: "rem-status",
};

/**
 * Tiny chip that shows a label + value and a copy-to-clipboard button.
 * Used in the "Refill source" mini-section so vets can paste pharmacy / Rx#
 * straight into the VA pharmacy portal in one tap.
 */
function CopyChip({ icon: Icon, label, value, testId }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  async function copy() {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(String(value));
      else {
        // Fallback for older browsers / non-https previews.
        const ta = document.createElement("textarea");
        ta.value = String(value);
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // Clipboard API can fail in iframes or when the page lost focus —
      // user can still read & copy the value manually, so this is non-fatal.
      console.warn("Rx copy-to-clipboard failed", err);
    }
  }
  return (
    <button
      type="button"
      className="rx-copy-chip"
      onClick={copy}
      title={`Copy ${label}`}
      data-testid={testId}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "2px 8px",
        background: copied ? "color-mix(in srgb, #10B981 12%, var(--surface))" : "var(--surface-2, #F1F5F9)",
        border: `1px solid ${copied ? "#10B981" : "var(--border)"}`,
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        color: "var(--text-primary)",
        cursor: "pointer",
        transition: "background-color 180ms ease-out, border-color 180ms ease-out",
        maxWidth: "100%",
      }}
    >
      <Icon size={11} weight="bold" />
      <span style={{ fontSize: 10, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</span>
      <span className="mono" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>{value}</span>
      {copied
        ? <CheckCircle size={11} weight="fill" color="#10B981" />
        : <Copy size={11} weight="bold" color="var(--text-secondary)" />}
    </button>
  );
}

/**
 * Tiny chip showing the most-recent verification verdict for an Rx ("match"
 * means the bottle barcode matched the saved value; "replaced" means the
 * user accepted a new barcode after a mismatch). Renders the relative
 * "verified X days ago" stamp so vets can tell at a glance whether the
 * record is fresh.
 */
function VerifiedChip({ verdict, at, testId }) {
  const verdictMap = {
    match: { label: "Verified", bg: "color-mix(in srgb, #10B981 14%, var(--surface))", border: "#10B981", color: "#047857" },
    replaced: { label: "Replaced", bg: "color-mix(in srgb, #d97706 12%, var(--surface))", border: "#d97706", color: "#b45309" },
  };
  const cfg = verdictMap[verdict];
  if (!cfg) return null;
  let rel = "";
  if (at) {
    try {
      const days = Math.floor((Date.now() - new Date(at).getTime()) / 86_400_000);
      rel = days === 0 ? "today" : days === 1 ? "1d ago" : `${days}d ago`;
    } catch { /* ignore parse errors */ }
  }
  return (
    <span
      data-testid={testId}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "2px 8px",
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        color: cfg.color,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
      title={at ? `Verified at ${new Date(at).toLocaleString()}` : cfg.label}
    >
      <CheckCircle size={10} weight="fill" />
      <span>{cfg.label}</span>
      {rel && <span style={{ opacity: 0.75, fontWeight: 600 }}>· {rel}</span>}
    </span>
  );
}

/**
 * Autocomplete-enabled medication name input. Suggests matching drug names
 * (generic + brand) from the local catalog as the user types. The user can
 * pick a suggestion (mouse, ↵, or arrow keys) or just keep typing — the input
 * value always tracks whatever they typed, never what they "almost" picked.
 */
function DrugNameInput({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const matches = useMemo(() => findDrugMatches(value, 8), [value]);

  function pick(item) {
    onChange(item.name);
    setOpen(false);
    setActive(0);
  }

  function onKeyDown(e) {
    if (!open || matches.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % matches.length); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setActive((a) => (a - 1 + matches.length) % matches.length); }
    else if (e.key === "Enter")     { e.preventDefault(); pick(matches[active]); }
    else if (e.key === "Escape")    { setOpen(false); }
  }

  return (
    <div style={{ position: "relative" }} data-testid="rx-name-autocomplete">
      <input
        className="field-input"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120) /* allow click on suggestion */}
        onKeyDown={onKeyDown}
        placeholder="e.g., Sertraline"
        data-testid="rx-name"
        autoComplete="off"
        spellCheck="false"
      />
      {open && matches.length > 0 && (
        <ul
          role="listbox"
          data-testid="rx-name-suggestions"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 50,
            marginTop: 2,
            padding: 4,
            background: "var(--surface, #fff)",
            border: "1px solid var(--border)",
            borderRadius: 4,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
            listStyle: "none",
            maxHeight: 260,
            overflowY: "auto",
          }}
        >
          {matches.map((m, idx) => (
            <li
              key={m.name}
              role="option"
              aria-selected={idx === active}
              onMouseDown={(e) => e.preventDefault() /* keep focus on input */}
              onClick={() => pick(m)}
              onMouseEnter={() => setActive(idx)}
              data-testid={`rx-name-suggestion-${idx}`}
              style={{
                padding: "6px 10px",
                borderRadius: 3,
                cursor: "pointer",
                display: "flex",
                gap: 8,
                alignItems: "baseline",
                background: idx === active ? "var(--surface-2, #F1F5F9)" : "transparent",
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>{m.name}</span>
              {m.brand && m.brand.length > 0 && (
                <span
                  className="mono"
                  aria-label={`also known as ${m.brand.slice(0, 2).join(", ")}`}
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-secondary)",
                    background: "var(--surface-2, #F1F5F9)",
                    padding: "1px 6px",
                    borderRadius: 3,
                    marginLeft: 6,
                  }}
                >
                  {m.brand.slice(0, 2).join(" · ")}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function emptyRx() {
  return {
    medName: "",
    // Legacy combined "strength + directions". Kept for back-compat in saved
    // records that pre-date the split. New scans populate `strength` +
    // `directions` separately; UI prefers the split fields when present.
    dosage: "",
    strength: "",
    directions: "",
    prescriber: "",
    pharmacy: "",
    rxNumber: "",
    quantity: "",
    // Raw decoded barcode payload (e.g. "RX123456789" or a GS1 string).
    // Persisted so we can show provenance, support label re-verification,
    // and survive cases where the Rx number on the bottle doesn't match
    // the barcode contents (relabelled bottles, repackaging, etc.).
    barcodeData: "",
    barcodeFormat: "",  // e.g. "CODE_128", "DATA_MATRIX", "QR_CODE"
    lastFilled: toLocalDateInputValue(),
    daysSupply: 30,
    refillsRemaining: 0,
    notes: "",
    archived: false,
    // Scan provenance fields (written by the Rx Scan Review modal):
    scanConfidence: null,
    scannedFields: null,
    learnedFromPharmacy: "",
  };
}

function RxForm({ initial, onCancel, onSave }) {
  const [form, setForm] = useState(initial || emptyRx());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [scanning, setScanning] = useState(false);
  // History of each scan, newest first. Each entry: { id, ok, text }.
  const [scanHistory, setScanHistory] = useState([]);
  const [showLiveScan, setShowLiveScan] = useState(false);
  const [reviewState, setReviewState] = useState(null);  // { extracted, barcode, barcodeFormat, barcodeSource, confidence }
  const fileInputRef = useRef(null);
  // We push a scan entry only after settle; this counter labels them "Scan 1:", "Scan 2:" etc.
  const scanCount = scanHistory.length;

  function patch(k, v) { setForm((p) => ({ ...p, [k]: v })); }

  /**
   * After the review modal confirms (or the user cancels), merge the
   * confirmed payload into the form. Non-empty incoming values overwrite
   * the form fields — the user has just explicitly approved them.
   */
  function applyConfirmedPayload(payload) {
    setForm((prev) => {
      const next = { ...prev };
      const textKeys = ["medName", "strength", "directions", "prescriber", "pharmacy",
                        "rxNumber", "quantity", "lastFilled", "notes",
                        "barcodeData", "barcodeFormat", "learnedFromPharmacy"];
      for (const k of textKeys) {
        if (payload[k] !== undefined && payload[k] !== "") next[k] = payload[k];
      }
      // Numeric fields — only overwrite when the scan gave us a real value.
      if (typeof payload.daysSupply === "number" && payload.daysSupply > 0) next.daysSupply = payload.daysSupply;
      if (typeof payload.refillsRemaining === "number" && payload.refillsRemaining > 0) next.refillsRemaining = payload.refillsRemaining;
      // Provenance
      if (payload.scanConfidence) next.scanConfidence = payload.scanConfidence;
      if (payload.scannedFields) next.scannedFields = payload.scannedFields;
      return next;
    });
    pushScanEntry({ ok: true, text: "Scan confirmed — review the form below, then save." });
  }

  function pushScanEntry(entry) {
    setScanHistory((prev) => [{ id: Date.now() + Math.random(), ...entry }, ...prev].slice(0, 5));
  }

  /**
   * Two-layer scan pipeline: native BarcodeDetector → ZXing fallback for
   * the barcode, GPT-5.2 Vision for OCR (with optional pharmacy hint).
   * Opens the Rx Scan Review modal instead of auto-populating the form.
   */
  async function handleScanPick(e) {
    const f = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-pick of the same file
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(f.type)) {
      pushScanEntry({ ok: false, text: "Use a JPEG, PNG, or WEBP image." });
      return;
    }
    setScanning(true); setErr("");
    let barcode = null;
    let barcodeFormat = null;
    let barcodeSource = null;
    let extracted = {};
    let ocrError = "";
    try {
      const smaller = await downsizeImage(f).catch(() => f);
      const bc = await decodeBarcodeDetailedFromFile(f).catch(() => null);
      if (bc) { barcode = bc.text; barcodeFormat = bc.format; barcodeSource = bc.source; }
      // Try to find a learned pharmacy pattern for the current pharmacy field —
      // if the user has filled it in already, pass it as a hint to the OCR call.
      let pharmacyHint = "";
      if (form.pharmacy && form.pharmacy.trim()) {
        try {
          const pat = await matchPharmacyPattern(form.pharmacy.trim());
          if (pat) {
            pharmacyHint = `Pharmacy: ${pat.pharmacyName}\nPrior confirmed scans:\n` +
              (pat.examples || []).slice(0, 2).map((ex) => `- ${JSON.stringify(ex)}`).join("\n");
          }
        } catch { /* non-fatal — proceed without hint */ }
      }
      try { extracted = await scanPrescriptionImage(smaller, pharmacyHint); }
      catch (e3) { ocrError = e3.response?.data?.detail || e3.message || "OCR failed."; }
      if (ocrError && !barcode) {
        pushScanEntry({ ok: false, text: ocrError });
      } else {
        setReviewState({
          extracted,
          barcode,
          barcodeFormat,
          barcodeSource,
          confidence: extracted?.confidence || {},
        });
      }
    } finally {
      setScanning(false);
    }
  }

  /**
   * The live-camera scanner returns the decoded barcode + a JPEG Blob of the
   * exact frame that matched. We close the camera, OCR the frame in the
   * background, then open the review modal — same pattern as the file path.
   */
  async function handleLiveCapture({ barcode, barcodeFormat, frameBlob }) {
    setShowLiveScan(false);
    setScanning(true); setErr("");
    try {
      let extracted = {};
      let ocrError = "";
      if (frameBlob) {
        const raw = new File([frameBlob], "rx-live.jpg", { type: "image/jpeg" });
        const file = await downsizeImage(raw).catch(() => raw);
        try { extracted = await scanPrescriptionImage(file); }
        catch (e3) { ocrError = e3.response?.data?.detail || e3.message || "OCR failed."; }
      }
      if (ocrError && !barcode) {
        pushScanEntry({ ok: false, text: ocrError });
      } else {
        setReviewState({
          extracted,
          barcode,
          barcodeFormat,
          barcodeSource: "native-camera",
          confidence: extracted?.confidence || {},
        });
      }
    } finally {
      setScanning(false);
    }
  }

  // Live preview of next refill date
  const previewNext = rxNextRefillDate(form);
  const previewDays = rxDaysUntilRefill(form);

  async function submit() {
    if (!form.medName.trim()) { setErr("Medication name is required."); return; }
    if (!form.lastFilled) { setErr("Last-filled date is required."); return; }
    if (!form.daysSupply || form.daysSupply < 1) { setErr("Days supply must be at least 1."); return; }
    setBusy(true); setErr("");
    try { await onSave(form); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" data-testid="rx-form-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="modal-panel modal-panel--lg">
        <div className="modal-header">
          <Pill size={16} weight="bold" />
          <span>{initial?.id ? "EDIT PRESCRIPTION" : "NEW PRESCRIPTION"}</span>
          <button className="modal-close" onClick={onCancel}><X size={14} weight="bold" /></button>
        </div>
        <div className="modal-body modal-body--scroll">
          <div className="rx-scan-row" data-testid="rx-scan-row" style={{ marginBottom: 12, padding: "10px 12px", background: "color-mix(in srgb, var(--accent, #1D4ED8) 5%, var(--surface))", border: "1px solid color-mix(in srgb, var(--accent, #1D4ED8) 25%, transparent)", borderRadius: 4, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 220px", minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: "var(--text-primary)" }}>
                {scanCount === 0 ? "SCAN A PRESCRIPTION LABEL OR BARCODE" : `SCAN MORE ANGLES OF THE BOTTLE  ·  ${scanCount} done`}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>
                {scanCount === 0
                  ? "Snap or upload a photo of the bottle, pharmacy label, or barcode. We transcribe the text and decode the barcode — the image is never saved."
                  : "Each new scan only fills BLANK fields and never overwrites what you already have. Great for stitching the barcode + the front of the label together."}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              capture="environment"
              onChange={handleScanPick}
              style={{ display: "none" }}
              data-testid="rx-scan-file"
            />
            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={() => setShowLiveScan(true)}
              disabled={scanning}
              data-testid="rx-live-scan-btn"
              title="Hold a barcode up to your camera"
            >
              <VideoCamera size={12} weight="bold" />
              {scanCount === 0 ? " LIVE SCAN" : " LIVE SCAN +"}
            </button>
            <button
              type="button"
              className="btn btn--accent btn--sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={scanning}
              data-testid="rx-scan-btn"
            >
              {scanning ? <Spinner size={12} weight="bold" className="spin" /> : <Camera size={12} weight="bold" />}
              {scanning
                ? " SCANNING…"
                : scanCount === 0 ? " SCAN PHOTO" : " ADD ANOTHER PHOTO"}
            </button>
          </div>
          {scanHistory.length > 0 && (
            <div
              className="rx-scan-history"
              data-testid="rx-scan-history"
              style={{ marginBottom: 10, display: "flex", flexDirection: "column", gap: 4 }}
            >
              {scanHistory.map((entry, idx) => (
                <div
                  key={entry.id}
                  className={entry.ok ? "form-report" : "form-report form-report--err"}
                  data-testid={`rx-scan-note-${idx}`}
                  style={{ display: "flex", alignItems: "flex-start", gap: 8 }}
                >
                  <span
                    className="mono"
                    style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
                      padding: "1px 6px", borderRadius: 3,
                      background: "var(--surface-2, #F1F5F9)",
                      color: "var(--text-secondary)",
                      flexShrink: 0,
                    }}
                  >
                    SCAN {scanHistory.length - idx}
                  </span>
                  <span style={{ flex: 1 }}>{entry.text}</span>
                </div>
              ))}
            </div>
          )}
          <div className="form-grid">
            <label className="field">
              <span className="field-label">MEDICATION NAME *</span>
              <DrugNameInput
                value={form.medName}
                onChange={(v) => patch("medName", v)}
              />
            </label>
            <label className="field">
              <span className="field-label">STRENGTH / DOSE</span>
              <input className="field-input" value={form.strength || form.dosage || ""} onChange={(e) => { patch("strength", e.target.value); if (form.dosage) patch("dosage", ""); }} placeholder="e.g., 50 mg" data-testid="rx-strength" />
            </label>
            <label className="field">
              <span className="field-label">DIRECTIONS (SIG)</span>
              <input className="field-input" value={form.directions || ""} onChange={(e) => patch("directions", e.target.value)} placeholder="Take by mouth three times daily with food" data-testid="rx-directions" />
            </label>
            <label className="field">
              <span className="field-label">PRESCRIBER</span>
              <input className="field-input" value={form.prescriber} onChange={(e) => patch("prescriber", e.target.value)} placeholder="Dr. name or VA clinic" data-testid="rx-prescriber" />
            </label>
            <label className="field">
              <span className="field-label">PHARMACY</span>
              <input className="field-input" value={form.pharmacy} onChange={(e) => patch("pharmacy", e.target.value)} placeholder="e.g., VA Mail Order Pharmacy" data-testid="rx-pharmacy" />
            </label>
            <label className="field">
              <span className="field-label">RX NUMBER</span>
              <input className="field-input mono" value={form.rxNumber} onChange={(e) => patch("rxNumber", e.target.value)} placeholder="e.g., 1234567" data-testid="rx-number" />
            </label>
            <label className="field">
              <span className="field-label">QUANTITY</span>
              <input className="field-input" value={form.quantity || ""} onChange={(e) => patch("quantity", e.target.value)} placeholder="e.g., 30 tablets" data-testid="rx-quantity" />
            </label>
            <label className="field">
              <span className="field-label" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Barcode size={11} weight="bold" /> BARCODE DATA
              </span>
              <input
                className="field-input mono"
                value={form.barcodeData || ""}
                onChange={(e) => patch("barcodeData", e.target.value)}
                placeholder="Scan or paste — e.g., 8901234567 (Code 128, GS1, Data Matrix)"
                data-testid="rx-barcode-data"
              />
            </label>
            <label className="field">
              <span className="field-label">BARCODE FORMAT</span>
              <select
                className="field-input mono"
                value={form.barcodeFormat || ""}
                onChange={(e) => patch("barcodeFormat", e.target.value)}
                data-testid="rx-barcode-format"
              >
                <option value="">— Auto-detected by scan —</option>
                <option value="CODE_128">Code 128</option>
                <option value="CODE_39">Code 39</option>
                <option value="DATA_MATRIX">Data Matrix</option>
                <option value="QR_CODE">QR Code</option>
                <option value="EAN_13">EAN-13</option>
                <option value="UPC_A">UPC-A</option>
                <option value="PDF_417">PDF417</option>
                <option value="OTHER">Other</option>
              </select>
            </label>
            <label className="field">
              <span className="field-label">REFILLS REMAINING</span>
              <input type="number" min="0" max="99" className="field-input mono" value={form.refillsRemaining} onChange={(e) => patch("refillsRemaining", Number(e.target.value))} data-testid="rx-refills" />
            </label>
            <label className="field">
              <span className="field-label">LAST FILLED *</span>
              <input type="date" className="field-input mono" value={form.lastFilled} onChange={(e) => patch("lastFilled", e.target.value)} data-testid="rx-lastfilled" />
            </label>
            <label className="field">
              <span className="field-label">DAYS SUPPLY *</span>
              <input type="number" min="1" max="365" className="field-input mono" value={form.daysSupply} onChange={(e) => patch("daysSupply", Number(e.target.value))} data-testid="rx-days-supply" />
            </label>
            <label className="field field--full">
              <span className="field-label">NOTES</span>
              <textarea className="field-input" rows={2} value={form.notes} onChange={(e) => patch("notes", e.target.value)} placeholder="e.g., take with food, side effects to watch for" data-testid="rx-notes" />
            </label>
          </div>
          <div className="rx-preview" data-testid="rx-preview">
            <div className="rx-preview-label">Auto-calculated next refill:</div>
            <div className="rx-preview-value mono">
              {previewNext ? `${previewNext.toLocaleDateString()} — ${formatRelativeDay(previewNext)}` : "—"}
              {previewDays !== null && previewDays >= 0 && previewDays <= 7 && (
                <span className="rem-status rem-status--due" style={{ marginLeft: 8 }}>ALERT — REFILL SOON</span>
              )}
              {previewDays !== null && previewDays < 0 && (
                <span className="rem-status rem-status--overdue" style={{ marginLeft: 8 }}>OVERDUE</span>
              )}
            </div>
          </div>
          {err && <div className="form-report form-report--err" data-testid="rx-error">{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn--outline" onClick={onCancel} disabled={busy}>CANCEL</button>
          <button className="btn btn--accent" onClick={submit} disabled={busy} data-testid="rx-save">
            <Check size={14} weight="bold" /> {busy ? "Saving..." : "SAVE"}
          </button>
        </div>
      </div>
      {showLiveScan && (
        <LiveBarcodeScanner
          onCapture={handleLiveCapture}
          onClose={() => setShowLiveScan(false)}
        />
      )}
      {reviewState && (
        <RxScanReviewModal
          initial={reviewState.extracted}
          barcode={reviewState.barcode}
          barcodeFormat={reviewState.barcodeFormat}
          barcodeSource={reviewState.barcodeSource}
          confidence={reviewState.confidence}
          onCancel={() => setReviewState(null)}
          onConfirm={(payload) => {
            applyConfirmedPayload(payload);
            setReviewState(null);
          }}
        />
      )}
    </div>
  );
}

export default function PrescriptionsView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [verifyRx, setVerifyRx] = useState(null);
  // Medication Scan Assistant modal — multi-photo capture session.
  const [showMsa, setShowMsa] = useState(false);

  async function load() {
    setLoading(true);
    setItems(await listPrescriptions());
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function handleSave(payload) {
    if (editing?.id) await updatePrescription(editing.id, payload);
    else await createPrescription(payload);
    setShowForm(false); setEditing(null);
    await load();
  }
  async function handleDelete(item) {
    if (!window.confirm(`Delete prescription "${item.medName}"?`)) return;
    await deletePrescription(item.id);
    await load();
    showToast({
      message: `Prescription "${item.medName}" deleted.`,
      duration: 6000,
      action: {
        label: "UNDO",
        onClick: async () => {
          try {
            const { id, user_id, ...payload } = item;
            void id; void user_id;
            await createPrescription(payload);
            await load();
            showToast({ variant: "success", message: "Prescription restored.", duration: 3000 });
          } catch (e) {
            showToast({ variant: "error", message: `Restore failed: ${e.message}` });
          }
        },
      },
    });
  }
  async function toggleArchive(item) {
    await updatePrescription(item.id, { archived: !item.archived });
    await load();
  }
  async function quickRefill(item) {
    // Record a refill by setting lastFilled = today, decrement refillsRemaining
    const updates = {
      lastFilled: new Date().toISOString().slice(0, 10),
      refillsRemaining: Math.max(0, (item.refillsRemaining || 0) - 1),
    };
    await updatePrescription(item.id, updates);
    await load();
  }

  /**
   * Apply a verification-flow patch coming out of <RefillVerifyModal>.
   * Re-loads the list so verdict chips / refill counts update instantly.
   */
  async function handleVerifyPatch(id, patch) {
    await updatePrescription(id, patch);
    await load();
  }

  const visible = items.filter((i) => showArchived || !i.archived);
  const dueSoon = items.filter((i) => !i.archived && ["overdue", "due_soon"].includes(rxStatus(i))).length;

  return (
    <div className="view-grid" data-testid="prescriptions-view">
      <div className="panel">
        <div className="panel-header">
          <Pill size={18} weight="bold" />
          <span>PRESCRIPTIONS</span>
          <span className="header-count mono">{items.filter((i) => !i.archived).length}</span>
          {dueSoon > 0 && (
            <span className="rem-status rem-status--due" data-testid="rx-due-count" style={{ marginLeft: 8 }}>
              {dueSoon} REFILL{dueSoon === 1 ? "" : "S"} NEEDED
            </span>
          )}
          <button
            className="btn btn--outline btn--sm"
            onClick={() => setShowMsa(true)}
            data-testid="scan-medication-btn"
            title="Capture multiple photos of a bottle to auto-fill the form"
            style={{ marginLeft: "auto" }}
          >
            <Sparkle size={12} weight="bold" /> SCAN MEDICATION
          </button>
          <button
            className="btn btn--accent btn--sm"
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="add-rx-btn"
          >
            <Plus size={12} weight="bold" /> NEW PRESCRIPTION
          </button>
        </div>
        <div className="panel-body">
          <p className="panel-desc">Enter your last-filled date and days supply — the next refill date is auto-calculated. You&apos;ll see an in-app alert when you&apos;re within 7 days of running out.</p>
          <label className="toggle-row toggle-row--inline">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="rx-show-archived" />
            <span>Show archived</span>
          </label>
        </div>
      </div>

      <div className="panel">
        <div className="panel-body" style={{ padding: 0 }}>
          {loading && <div className="admin-empty">Loading...</div>}
          {!loading && visible.length === 0 && (
            <div className="empty-state" data-testid="rx-empty">
              <Pill size={28} weight="duotone" />
              <div className="empty-state-title">No prescriptions tracked yet</div>
              <div className="empty-state-desc">
                Add your meds and we&apos;ll auto-calculate refill dates, alert you 7 days before you run out, and offer a one-click <strong>REFILLED</strong> button to keep the timeline current.
              </div>
              <button
                className="btn btn--accent"
                onClick={() => { setEditing(null); setShowForm(true); }}
                data-testid="rx-empty-cta"
              >
                <Plus size={12} weight="bold" /> ADD YOUR FIRST PRESCRIPTION
              </button>
            </div>
          )}
          {!loading && visible.length > 0 && (
            <div className="table-scroll">
              <div className="table-scroll-hint">← scroll →</div>
            <table className="admin-table table--cards-on-mobile" data-testid="rx-table">
              <thead>
                <tr>
                  <th>MEDICATION</th>
                  <th>DOSAGE</th>
                  <th>LAST FILLED</th>
                  <th className="mono">DAYS SUPPLY</th>
                  <th>NEXT REFILL</th>
                  <th className="mono">REFILLS LEFT</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((rx) => {
                  const next = rxNextRefillDate(rx);
                  const days = rxDaysUntilRefill(rx);
                  const status = rxStatus(rx);
                  return (
                    <tr key={rx.id} data-testid={`rx-row-${rx.id}`} className={rx.archived ? "rx-archived" : ""}>
                      <td data-label="MEDICATION">
                        <div style={{ fontWeight: 700 }}>{rx.medName}</div>
                        {rx.prescriber && <div className="small">{rx.prescriber}</div>}
                        {(rx.pharmacy || rx.rxNumber || rx.barcodeData) && (
                          <div
                            className="rx-refill-source"
                            data-testid={`rx-refill-source-${rx.id}`}
                            style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}
                          >
                            <CopyChip
                              icon={Storefront}
                              label="Pharmacy"
                              value={rx.pharmacy}
                              testId={`rx-copy-pharmacy-${rx.id}`}
                            />
                            <CopyChip
                              icon={Hash}
                              label="Rx#"
                              value={rx.rxNumber}
                              testId={`rx-copy-rxnumber-${rx.id}`}
                            />
                            <CopyChip
                              icon={Barcode}
                              label={rx.barcodeFormat ? `Barcode (${rx.barcodeFormat.replace(/_/g, " ")})` : "Barcode"}
                              value={rx.barcodeData}
                              testId={`rx-copy-barcode-${rx.id}`}
                            />
                            {rx.lastVerifyVerdict && (
                              <VerifiedChip verdict={rx.lastVerifyVerdict} at={rx.lastVerifiedAt} testId={`rx-verified-chip-${rx.id}`} />
                            )}
                          </div>
                        )}
                      </td>
                      <td data-label="DOSAGE">
                        {(rx.strength || rx.directions)
                          ? [rx.strength, rx.directions].filter(Boolean).join(" — ")
                          : (rx.dosage || "—")}
                      </td>
                      <td data-label="LAST FILLED" className="mono small">{rx.lastFilled}</td>
                      <td data-label="DAYS SUPPLY" className="mono">{rx.daysSupply}</td>
                      <td data-label="NEXT REFILL">
                        <div className="mono small">{next ? next.toLocaleDateString() : "—"}</div>
                        <div className="small">{next ? formatRelativeDay(next) : ""}</div>
                      </td>
                      <td data-label="REFILLS LEFT" className="mono">{rx.refillsRemaining}</td>
                      <td data-label="STATUS"><span className={STATUS_CLASS[status]} data-testid={`rx-status-${rx.id}`}>{STATUS_LABEL[status]}</span></td>
                      <td>
                        <div className="row-actions">
                          {!rx.archived && (status === "overdue" || status === "due_soon") && (
                            <button className="btn btn--accent btn--sm" onClick={() => quickRefill(rx)} title="Record refill (sets last-filled to today)" data-testid={`rx-refill-${rx.id}`}>
                              <ArrowCounterClockwise size={11} weight="bold" /> REFILLED
                            </button>
                          )}
                          {!rx.archived && (
                            <button
                              className="btn btn--outline btn--sm"
                              onClick={() => setVerifyRx(rx)}
                              title="Scan the bottle to verify this refill"
                              data-testid={`rx-verify-${rx.id}`}
                            >
                              <ShieldCheck size={11} weight="bold" /> VERIFY
                            </button>
                          )}
                          <button className="btn btn--outline btn--sm" onClick={() => { setEditing(rx); setShowForm(true); }} data-testid={`rx-edit-${rx.id}`}>
                            <PencilSimple size={11} weight="bold" />
                          </button>
                          <button
                            className="btn btn--outline btn--sm"
                            onClick={() => downloadIcs(getPrescriptionIcsUrl(rx.id), `rx-${(rx.medName || "rx").replace(/[^A-Za-z0-9_-]+/g, "-").slice(0, 30)}.ics`)}
                            title="Download .ics for calendar refill reminder"
                            data-testid={`rx-ics-${rx.id}`}
                          >
                            <CalendarPlus size={11} weight="bold" />
                          </button>
                          <button className="btn btn--outline btn--sm" onClick={() => toggleArchive(rx)} title={rx.archived ? "Restore" : "Archive"} data-testid={`rx-archive-${rx.id}`}>
                            <Archive size={11} weight="bold" />
                          </button>
                          <button className="btn btn--danger btn--sm" onClick={() => handleDelete(rx)} data-testid={`rx-delete-${rx.id}`}>
                            <Trash size={11} weight="bold" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <RxForm
          initial={editing}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSave={handleSave}
        />
      )}

      {verifyRx && (
        <RefillVerifyModal
          rx={verifyRx}
          onClose={() => setVerifyRx(null)}
          onPatch={handleVerifyPatch}
        />
      )}

      {showMsa && (
        <MedicationScanAssistant
          onCancel={() => setShowMsa(false)}
          onConfirm={async (payload) => {
            // Save directly — the assistant has already collected user
            // confirmation. Mirror createPrescription's field shape: drop
            // any UI-only keys the API doesn't accept.
            const now = new Date();
            const draft = {
              ...payload,
              lastFilled: payload.lastFilled || now.toISOString().slice(0, 10),
              daysSupply: Number(payload.daysSupply) || 30,
              refillsRemaining: Number(payload.refillsRemaining) || 0,
              linkedIssueId: "",
              notes: payload.notes || "",
              archived: false,
              learnedFromPharmacy: payload.pharmacy || "",
            };
            try {
              await createPrescription(draft);
              await load();
              setShowMsa(false);
              showToast({ variant: "success", message: `Saved ${draft.medName || "prescription"}. Tap to review fields.`, duration: 3500 });
            } catch (err) {
              const msg = err?.response?.data?.detail || err.message || "Save failed.";
              showToast({ variant: "error", message: msg, duration: 4500 });
            }
          }}
        />
      )}

      <button
        type="button"
        className="mobile-fab"
        onClick={() => { setEditing(null); setShowForm(true); }}
        aria-label="New prescription"
        data-testid="rx-mobile-fab"
      >
        <Plus size={20} weight="bold" />
      </button>
    </div>
  );
}
